import { useEffect, useRef } from "react";
import { getAdminIdFromToken } from "../utils/getAdminId";
import { API_BASE_URL } from "../hooks/request";
import AdminService from "../services/admin.services";

interface UseOrderSSEProps {
  onNewOrder: (order: any) => void;
  onOrderUpdate?: (order: any) => void;
  playSound?: boolean;
  openingTime?: string; // Format: "HH:MM"
  closingTime?: string; // Format: "HH:MM"
}

const SSE_BASE_URL =
  process.env.REACT_APP_SSE_URL || API_BASE_URL.replace("/api/v1", "");

/**
 * Check if current time is within business hours
 * @param openingTime - Opening time in "HH:MM" format (default: "08:00")
 * @param closingTime - Closing time in "HH:MM" format (default: "22:00")
 * @returns true if current time is within business hours
 */
const isWithinBusinessHours = (
  openingTime: string = "08:00",
  closingTime: string = "22:00"
): boolean => {
  // Validate input format (HH:MM)
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(openingTime) || !timeRegex.test(closingTime)) {
    // Fallback to defaults if invalid
    openingTime = "08:00";
    closingTime = "22:00";
  }

  const now = new Date();
  const currentHours = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTime = currentHours * 60 + currentMinutes;

  const [openHours, openMinutes] = openingTime.split(":").map(Number);
  const openingTimeMinutes = openHours * 60 + openMinutes;

  const [closeHours, closeMinutes] = closingTime.split(":").map(Number);
  const closingTimeMinutes = closeHours * 60 + closeMinutes;

  // Handle overnight hours (e.g., 22:00 to 08:00)
  if (closingTimeMinutes < openingTimeMinutes) {
    return (
      currentTime >= openingTimeMinutes || currentTime < closingTimeMinutes
    );
  }

  // Handle day hours (e.g., 08:00 to 22:00)
  return currentTime >= openingTimeMinutes && currentTime < closingTimeMinutes;
};

/**
 * Custom hook for Server-Sent Events (SSE) to receive real-time order updates
 *
 * @param onNewOrder - Callback function when a new order is received
 * @param onOrderUpdate - Optional callback function when an order is updated
 * @param playSound - Whether to play notification sound (default: true)
 */
export const useOrderSSE = ({
  onNewOrder,
  onOrderUpdate,
  playSound = true,
  openingTime = "08:00",
  closingTime = "22:00",
}: UseOrderSSEProps) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const checkHoursIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUnlockedRef = useRef<boolean>(false); // Track if audio is unlocked for autoplay
  const isPlayingRef = useRef<boolean>(false); // Lock to prevent overlapping sounds
  const retryCountRef = useRef<number>(0); // Track retry attempts for exponential backoff
  const maxRetries = 20; // Maximum number of retry attempts
  const retryDelay = 5000; // Initial retry delay in milliseconds
  const onNewOrderRef = useRef(onNewOrder);
  const onOrderUpdateRef = useRef(onOrderUpdate);
  const unlockListenersRef = useRef<
    Array<{ event: string; handler: () => void }>
  >([]); // Track unlock listeners for cleanup
  // Fetch current admin id via API; fallback to token if needed
  const fetchCurrentAdminId = async (): Promise<string | null> => {
    try {
      const adminService = new AdminService();
      const profile = await adminService.getCurrentAdmin();
      const id = profile?._id || profile?.id || profile?.adminId || null;
      if (id) return id;
    } catch (e) {
      // Failed to fetch admin via API, will fallback to token
    }
    // Final fallback: decode from token (legacy)
    return getAdminIdFromToken();
  };

  // Keep callbacks in refs so they're always current
  useEffect(() => {
    onNewOrderRef.current = onNewOrder;
    onOrderUpdateRef.current = onOrderUpdate;
  }, [onNewOrder, onOrderUpdate]);

  // Function to pause and reset sound (exposed for manual control)
  const pauseAndResetSound = async () => {
    if (!audioRef.current) return;

    try {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      isPlayingRef.current = false;
    } catch (error) {
      // Audio pause/reset error - silently fail
    }
  };

  // Function to unlock audio using the actual audio element we'll use for playback
  const unlockAudio = async (
    audioElement: HTMLAudioElement
  ): Promise<boolean> => {
    if (audioUnlockedRef.current) {
      return true;
    }

    try {
      // Set very low volume for unlock
      const originalVolume = audioElement.volume;
      audioElement.volume = 0.01;

      // Wait for audio to be ready
      if (audioElement.readyState < 2) {
        await new Promise<void>((resolve) => {
          if (audioElement.readyState >= 2) {
            resolve();
          } else {
            audioElement.addEventListener("canplay", () => resolve(), {
              once: true,
            });
            setTimeout(() => resolve(), 500);
          }
        });
      }

      // Try to play and immediately pause to unlock
      await audioElement.play();
      audioElement.pause();
      audioElement.currentTime = 0;
      audioElement.volume = originalVolume; // Restore volume

      audioUnlockedRef.current = true;
      return true;
    } catch (error: any) {
      return false;
    }
  };

  // Function to play notification sound with proper error handling
  const playNotificationSound = async () => {
    if (!playSound) {
      return;
    }

    // Prevent overlapping sounds - if already playing, stop and reset
    if (isPlayingRef.current && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      // Don't clear the ref, we'll reuse it
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Set playing lock
    isPlayingRef.current = true;

    try {
      // Reuse existing audio element or create new one
      let audio: HTMLAudioElement;
      if (!audioRef.current) {
        // Use consistent path - try multiple possible locations
        const audioPaths = [
          "/assets/sounds/notification.mp3",
          "/sounds/notification.mp3",
          "/notification.mp3",
        ];
        
        // Try to find the audio file
        let audioUrl = audioPaths[0];
        audio = new Audio(audioUrl);
        audio.preload = "auto";
        audio.volume = 1.0;
        audioRef.current = audio;
      } else {
        // Reset existing audio
        audio = audioRef.current;
        audio.currentTime = 0;
        audio.volume = 1.0;
      }

      // Add error listener to catch loading errors
      audio.addEventListener("error", (e) => {
        // Audio loading error - try fallback path if first fails
        if (audio.src.includes("/assets/sounds/") && !audioRef.current?.src.includes("/sounds/")) {
          console.warn("Audio file not found at /assets/sounds/, trying fallback...");
          const fallbackAudio = new Audio("/sounds/notification.mp3");
          fallbackAudio.preload = "auto";
          fallbackAudio.volume = 1.0;
          audioRef.current = fallbackAudio;
        }
      });

      // Wait for audio to be ready
      await new Promise<void>((resolve) => {
        if (audio.readyState >= 2) {
          resolve();
        } else {
          audio.addEventListener("canplay", () => resolve(), { once: true });
          setTimeout(() => resolve(), 1000); // Fallback timeout
        }
      });

      // Try to unlock if needed (use the actual audio element)
      if (!audioUnlockedRef.current) {
        await unlockAudio(audio);
      }

      // Reset to beginning and play
      audio.currentTime = 0;
      audio.volume = 1.0;

      // Try to play
      try {
        await audio.play();
      } catch (playError: any) {
        // If play failed and audio wasn't unlocked, try one more unlock
        if (
          !audioUnlockedRef.current &&
          (playError.name === "NotAllowedError" ||
            playError.name === "NotSupportedError")
        ) {
          const unlocked = await unlockAudio(audio);
          if (unlocked) {
            audio.currentTime = 0;
            await audio.play();
          } else {
            throw playError;
          }
        } else {
          throw playError;
        }
      }

      // Release lock when audio ends
      audio.addEventListener(
        "ended",
        () => {
          isPlayingRef.current = false;
          // Keep audio element for reuse
        },
        { once: true }
      );
    } catch (error: any) {
      isPlayingRef.current = false;
      // Error playing notification sound - silently fail
    }
  };

  useEffect(() => {
    // Clear any existing listeners first
    unlockListenersRef.current.forEach(({ event, handler }) => {
      document.removeEventListener(event, handler);
    });
    unlockListenersRef.current = [];

    // Try to unlock audio immediately on mount (if user has already interacted with page)
    if (playSound) {
      // Attempt immediate unlock (might work if user already interacted with page)
      const tempAudio = new Audio("/assets/sounds/notification.mp3");
      tempAudio.preload = "auto";
      unlockAudio(tempAudio).then(() => {
        // Clean up temp audio
        tempAudio.src = "";
        tempAudio.load();
      });

      // Setup user interaction listeners as backup (only if not already unlocked)
      const unlockOnInteraction = async () => {
        if (audioUnlockedRef.current) {
          // Remove listeners if already unlocked
          unlockListenersRef.current.forEach(({ event, handler }) => {
            document.removeEventListener(event, handler);
          });
          unlockListenersRef.current = [];
          return;
        }

        const tempAudio = new Audio("/assets/sounds/notification.mp3");
        tempAudio.preload = "auto";
        const unlocked = await unlockAudio(tempAudio);
        if (unlocked) {
          // Remove all listeners once unlocked successfully
          unlockListenersRef.current.forEach(({ event, handler }) => {
            document.removeEventListener(event, handler);
          });
          unlockListenersRef.current = [];
        }
        // Clean up temp audio
        tempAudio.src = "";
        tempAudio.load();
      };

      // Listen for any user interaction to unlock audio (as backup)
      const clickHandler = unlockOnInteraction;
      const touchHandler = unlockOnInteraction;
      const keyHandler = unlockOnInteraction;

      document.addEventListener("click", clickHandler);
      document.addEventListener("touchstart", touchHandler);
      document.addEventListener("keydown", keyHandler);

      unlockListenersRef.current.push(
        { event: "click", handler: clickHandler },
        { event: "touchstart", handler: touchHandler },
        { event: "keydown", handler: keyHandler }
      );
    }

    const establishConnection = async () => {
      // Check if within business hours before connecting
      if (!isWithinBusinessHours(openingTime, closingTime)) {
        return;
      }
      try {
        const adminId = await fetchCurrentAdminId();

        if (!adminId) {
          return;
        }

        // Close existing connection if any
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }

        // Clear any pending reconnection
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        const endpoint = `${SSE_BASE_URL}/api/events/admins/${adminId}`;

        const eventSource = new EventSource(endpoint);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          retryCountRef.current = 0; // Reset retry count on successful connection

          // Try to unlock audio when SSE connects (user has likely interacted by now)
          if (playSound && !audioUnlockedRef.current) {
            const tempAudio = new Audio("/assets/sounds/notification.mp3");
            tempAudio.preload = "auto";
            unlockAudio(tempAudio).then(() => {
              // Clean up temp audio
              tempAudio.src = "";
              tempAudio.load();
            });
          }
        };

        eventSource.addEventListener("connected", () => {
          // Connection established event
        });

        const handleNewOrderEvent = (order: any) => {
          try {
            // Call the callback using ref to ensure we have the latest version
            if (onNewOrderRef.current) {
              onNewOrderRef.current(order);
            }

            // Play notification sound with proper browser compatibility
            if (playSound) {
              playNotificationSound().catch(() => {
                // Error playing notification sound - silently fail
              });
            }
          } catch (error) {
            // Error handling new_order event - silently fail
          }
        };

        eventSource.addEventListener("new_order", (e) => {
          try {
            const order = JSON.parse(e.data);
            handleNewOrderEvent(order);
          } catch (error) {
            // Error parsing new_order event - silently fail
          }
        });

        eventSource.addEventListener("order_update", (e) => {
          try {
            const order = JSON.parse(e.data);

            // Call the update callback if provided, using ref
            if (onOrderUpdateRef.current) {
              onOrderUpdateRef.current(order);
            }
          } catch (error) {
            // Error parsing order_update event - silently fail
          }
        });

        eventSource.addEventListener("heartbeat", () => {
          // Reset reconnection timer when we receive heartbeat (connection is alive)
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        });

        eventSource.onerror = () => {
          // Note: SSE connections can error for various reasons:
          // - Network issues (will auto-reconnect)
          // - Server restarts (will auto-reconnect)
          // - Browser tab going to sleep (will auto-reconnect when active)

          // Check if within business hours before reconnecting
          if (!isWithinBusinessHours(openingTime, closingTime)) {
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            return;
          }

          // Retry connection with exponential backoff
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current += 1;
            const delay = retryDelay * Math.pow(2, retryCountRef.current - 1); // Exponential backoff

            reconnectTimeoutRef.current = setTimeout(() => {
              establishConnection();
            }, delay);
          }
        };
      } catch (error) {
        // Connection error - silently fail
      }
    };

    // Check business hours periodically and connect/disconnect accordingly
    const checkAndConnect = () => {
      const withinHours = isWithinBusinessHours(openingTime, closingTime);

      if (withinHours) {
        // Within business hours - ensure connection is active
        if (!eventSourceRef.current) {
          // Not connected - establish connection
          establishConnection();
        } else {
          // Already connected - verify it's still open
          // Note: If connection closed due to error, onerror handler will reconnect
          // This check ensures we reconnect if connection somehow closed without error
          const state = eventSourceRef.current.readyState;
          if (state === EventSource.CLOSED) {
            eventSourceRef.current = null; // Clear ref
            establishConnection();
          }
        }
      } else {
        // Outside business hours - disconnect if connected
        if (eventSourceRef.current) {
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          // Clear any pending reconnection
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        }
      }
    };

    // Check immediately
    checkAndConnect();

    // Check every 30 seconds to ensure connection stays active during business hours
    // This helps catch cases where connection closes without triggering error handler
    checkHoursIntervalRef.current = setInterval(checkAndConnect, 30000); // Check every 30 seconds

    // Cleanup on unmount
    return () => {
      // Cleanup audio interaction listeners
      unlockListenersRef.current.forEach(({ event, handler }) => {
        document.removeEventListener(event, handler);
      });
      unlockListenersRef.current = [];

      if (checkHoursIntervalRef.current) {
        clearInterval(checkHoursIntervalRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      audioUnlockedRef.current = false;
      isPlayingRef.current = false; // Reset playing lock
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playSound, openingTime, closingTime]); // Re-run when hours or playSound changes

  return {
    close: () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    },
    pauseAndResetSound, // Expose function to manually stop sound
  };
};
